import React from 'react';
import { Instagram, Mail } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const Footer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleScrollTo = (id) => {
    const sectionId = id.replace('#', '');
    if (location.pathname === '/') {
      // On est déjà sur Home : scroll natif direct
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // On navigue vers Home avec query param, Home se charge du scroll à l'arrivée
      navigate(`/?scrollTo=${sectionId}`);
    }
  };

  return (
    <footer className="bg-ink-950 bg-textured border-t border-white/10">
      <div className="container mx-auto px-6 py-14">
        <div className="grid md:grid-cols-3 gap-10">
          {/* Brand */}
          <div>
            <h3 className="font-display text-2xl uppercase tracking-wide mb-3">
              <span className="text-ember">AM</span>
              <span className="text-paper/40 mx-1">·</span>
              <span className="text-paper">Imagery</span>
            </h3>
            <p className="font-body text-gray-400 text-sm leading-relaxed max-w-xs">
              {t('footer.brand.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-mono text-amber mb-4 uppercase tracking-[0.15em] text-[11px]">
              {t('footer.quick_links.title')}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to="/"
                  className="font-body text-gray-400 hover:text-ember transition-colors text-sm"
                >
                  {t('footer.quick_links.home')}
                </Link>
              </li>
              <li>
                <Link
                  to="/portfolio"
                  className="font-body text-gray-400 hover:text-ember transition-colors text-sm"
                >
                  {t('footer.quick_links.portfolio')}
                </Link>
              </li>
              <li>
                <button
                  onClick={() => handleScrollTo('#about')}
                  className="font-body text-gray-400 hover:text-ember transition-colors text-sm"
                >
                  {t('footer.quick_links.about')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleScrollTo('#contact')}
                  className="font-body text-gray-400 hover:text-ember transition-colors text-sm"
                >
                  {t('footer.quick_links.contact')}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-mono text-amber mb-4 uppercase tracking-[0.15em] text-[11px]">
              {t('footer.contact.title')}
            </h4>
            <div className="space-y-3">
              <a
                href={`mailto:${t('home.contact.email_address')}`}
                className="flex items-center gap-3 font-body text-gray-400 hover:text-ember transition-colors text-sm group"
              >
                <Mail size={16} className="group-hover:scale-110 transition-transform" />
                {t('home.contact.email_address')}
              </a>
              <a
                href="https://instagram.com/a_m.imagery"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 font-body text-gray-400 hover:text-ember transition-colors text-sm group"
              >
                <Instagram size={16} className="group-hover:scale-110 transition-transform" />
                {t('home.contact.instagram_handle')}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-2 font-mono text-[11px] tracking-[0.08em] uppercase text-gray-500">
          <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
        </div>
      </div>
    </footer>
  );
};
